/**
 * AdBrowserModal - Main modal for browsing and selecting Meta ads
 *
 * Features:
 * - Portal-based overlay (follows ConfirmDeleteModal pattern)
 * - Date range filtering (defaults to last 90 days)
 * - Accordion tree for campaigns > ad sets > ads
 * - Checkbox selection for video and image ads
 * - Import button with selected count
 */

import { X, Loader2, Download, AlertCircle, RefreshCw } from 'lucide-react';
import { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@/components/ui/Button';
import { useCampaigns } from '@/hooks/useMetaBrowse';
import { cn } from '@/lib/utils';
import type { MetaDateRange, ImportJob } from '@/types/meta';

import { CampaignRow } from './CampaignRow';
import { DateRangePicker } from './DateRangePicker';
import { ImportProgress } from './ImportProgress';

// ============================================================================
// TYPES
// ============================================================================

export interface AdBrowserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (adIds: string[]) => void | Promise<void>;
  teamId: string;
  projectId: string;
  isImporting?: boolean;
  /** Active import job to show progress inline */
  activeJob?: ImportJob | null;
}

// ============================================================================
// HELPER: Get default date range (last 90 days)
// ============================================================================

function getDefaultDateRange(): MetaDateRange {
  const today = new Date();
  const ninetyDaysAgo = new Date(today);
  ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

  // Format as YYYY-MM-DD (toISOString always has 'T', so split is safe)
  const formatDate = (date: Date): string => date.toISOString().split('T')[0] as string;

  return {
    since: formatDate(ninetyDaysAgo),
    until: formatDate(today),
  };
}

// ============================================================================
// MODAL OVERLAY COMPONENT
// ============================================================================

function ModalOverlay({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClick();
        }
      }}
    >
      {children}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function AdBrowserModal({
  isOpen,
  onClose,
  onImport,
  teamId,
  projectId: _projectId,
  isImporting = false,
  activeJob = null,
}: AdBrowserModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  // State
  const [dateRange, setDateRange] = useState<MetaDateRange>(getDefaultDateRange);
  const [selectedAds, setSelectedAds] = useState<Set<string>>(new Set());

  // Fetch campaigns
  const {
    data: campaigns,
    isLoading: isLoadingCampaigns,
    isFetching: isFetchingCampaigns,
    error: campaignsError,
    refetch: refetchCampaigns,
  } = useCampaigns({
    teamId,
    dateRange,
    enabled: isOpen,
  });

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedAds(new Set());
      setDateRange(getDefaultDateRange());
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isImporting) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isImporting, onClose]);

  // Focus trap
  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen]);

  // Handle ad selection
  const handleSelectAd = (adId: string, selected: boolean) => {
    setSelectedAds((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(adId);
      } else {
        next.delete(adId);
      }
      return next;
    });
  };

  // Handle import
  const handleImport = async () => {
    if (selectedAds.size === 0) return;
    await onImport(Array.from(selectedAds));
  };

  // Count total video ads across all campaigns
  const totalVideoAds = useMemo(() => {
    if (!campaigns) return 0;
    return campaigns.reduce((sum, c) => sum + c.videoAdCount, 0);
  }, [campaigns]);

  if (!isOpen) return null;

  const modalContent = (
    <ModalOverlay onClick={onClose}>
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
        className={cn(
          'w-full max-w-4xl max-h-[80vh] bg-surface rounded-xl shadow-xl border border-border-subtle',
          'animate-in zoom-in-95 slide-in-from-bottom-2 duration-200',
          'focus:outline-none flex flex-col'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle">
          <div>
            <h2 id="modal-title" className="text-lg font-semibold text-primary">
              Import from Meta Ads
            </h2>
            <p className="text-sm text-muted mt-0.5">
              Select ads to import for analysis
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isImporting}
            className={cn(
              'p-2 rounded-lg text-muted hover:text-primary hover:bg-surface-subtle transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/50',
              'disabled:opacity-50 disabled:pointer-events-none'
            )}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-border-subtle bg-surface-subtle/50">
          <div className="flex items-center gap-3">
            <DateRangePicker
              value={dateRange}
              onChange={setDateRange}
              disabled={isLoadingCampaigns}
            />
            <button
              onClick={() => refetchCampaigns()}
              disabled={isFetchingCampaigns}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md',
                'text-sm font-medium text-secondary',
                'border border-border hover:bg-surface-subtle',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'transition-colors'
              )}
              title="Refresh campaign data"
            >
              <RefreshCw className={cn('h-4 w-4', isFetchingCampaigns && 'animate-spin')} />
              Refresh
            </button>
          </div>
          <div className="text-sm text-muted">
            {totalVideoAds > 0
              ? `${totalVideoAds} video ad${totalVideoAds !== 1 ? 's' : ''} found`
              : 'Expand ad sets to browse ads'}
          </div>
        </div>

        {/* Import Progress (shown inline when importing) */}
        {activeJob && (
          <div className="px-6 py-4 border-b border-border-subtle bg-surface-subtle/30">
            <ImportProgress job={activeJob} />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {isLoadingCampaigns ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="h-8 w-8 text-brand animate-spin" />
              <p className="mt-4 text-sm text-muted">Loading campaigns...</p>
            </div>
          ) : campaignsError ? (
            <div className="flex flex-col items-center justify-center py-16">
              <AlertCircle className="h-8 w-8 text-danger" />
              <p className="mt-4 text-sm text-danger">
                Failed to load campaigns. Please try again.
              </p>
              <p className="mt-1 text-xs text-muted">
                {campaignsError instanceof Error ? campaignsError.message : 'Unknown error'}
              </p>
            </div>
          ) : campaigns && campaigns.length > 0 ? (
            <div>
              {campaigns.map((campaign) => (
                <CampaignRow
                  key={campaign.id}
                  campaign={campaign}
                  teamId={teamId}
                  dateRange={dateRange}
                  selectedAds={selectedAds}
                  onSelectAd={handleSelectAd}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16">
              <p className="text-sm text-muted">
                No campaigns found for the selected date range.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border-subtle">
          <div className="text-sm text-secondary">
            {selectedAds.size > 0 ? (
              <span className="font-medium">
                {selectedAds.size} ad{selectedAds.size !== 1 ? 's' : ''} selected
              </span>
            ) : (
              <span className="text-muted">Select ads to import</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={isImporting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              disabled={selectedAds.size === 0 || isImporting}
              leftIcon={
                isImporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )
              }
            >
              {isImporting
                ? 'Importing...'
                : `Import ${selectedAds.size > 0 ? selectedAds.size : ''} Creative${selectedAds.size !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      </div>
    </ModalOverlay>
  );

  return createPortal(modalContent, document.body);
}
